import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRegion } from "@/contexts/RegionContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Calendar, 
  Download, 
  RefreshCw, 
  PoundSterling, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Search,
  Loader2,
  ArrowRight,
  Banknote,
  Users,
  Percent,
  Clock,
  Link2,
  Building2,
  X,
  Eye,
  EyeOff,
  CalendarDays,
  TrendingUp as TrendUp,
  Edit3,
  Save,
  Send,
  FileText,
  CheckCheck
} from "lucide-react";
import { matchPaymentsToLoans, markTransactionIrrelevant, manualMatchPayment, getCollectionsReportSummary, updateCollectionsReport, getLoanDiskStats, submitDailyReport, getReportPrefill, getArrearsLiveStats, matchP2PPayment, unmatchP2PPayment, getPrechaseStatus, getPrechaseFacts } from "@/lib/api";
import { toast } from "sonner";
import CollectionsPeriodAnalysis from "./CollectionsPeriodAnalysis";
import { BankFeedChip } from "./BankFeedChip";
import PreChaseWizard from "./PreChaseWizard";
import { DailyReportStats } from "./DailyReportStats";
import { AwaitingPaymentTable, QuietTag, baseSf } from "./AwaitingPaymentTable";
import { UnmatchedTransactionsTable } from "./UnmatchedTransactionsTable";
import { CoveredCollapsible, MatchedCollapsible } from "./MatchedCollapsible";
import CollectionReportTab from "./report/CollectionReportTab";

const SF_BLUE = "#3B70E0";


const EMPTY_REPORT_FORM = {
  loan_due: "",
  cash_collected: "",
  total_open_loans: "",
  total_receivables: "",
  total_arrears: "",
  principal_in_arrears: "",
  total_paid_in_arrears: "",
  arrears_count: "",
  arrears_1_7_days: "",
  arrears_8_30_days: "",
  arrears_30_plus_days: "",
  loan_book_maturity_pct: ""
};

// Dialog for posting daily collections report (with arrears autofill)
function SubmitDailyReportDialog({ selectedDate, open, onOpenChange, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [pullingArrears, setPullingArrears] = useState(false);
  const [arrearsSource, setArrearsSource] = useState(null);
  const [formData, setFormData] = useState(EMPTY_REPORT_FORM);

  // Report is for the previous working day (M–F) — Mondays post Friday's figures
  const reportDate = new Date(selectedDate);
  reportDate.setDate(reportDate.getDate() - 1);
  while (reportDate.getDay() === 0 || reportDate.getDay() === 6) {
    reportDate.setDate(reportDate.getDate() - 1);
  }
  const reportDateStr = reportDate.toISOString().split('T')[0];

  useEffect(() => {
    if (!open) return;
    setFormData(EMPTY_REPORT_FORM);
    setAutofilled(false);
    setArrearsSource(null);
    setPrefilling(true);
    getReportPrefill(reportDateStr)
      .then((res) => {
        if (res?.found && res?.arrears_filed) {
          const a = res.arrears;
          setFormData((prev) => ({
            ...prev,
            loan_due: res.loan_due ? String(res.loan_due) : prev.loan_due,
            cash_collected: res.cash_collected ? String(res.cash_collected) : prev.cash_collected,
            total_open_loans: a.total_open_loans ? String(a.total_open_loans) : "",
            total_receivables: a.total_receivables ? String(a.total_receivables) : "",
            total_arrears: a.total_arrears ? String(a.total_arrears) : "",
            principal_in_arrears: a.principal_in_arrears ? String(a.principal_in_arrears) : "",
            total_paid_in_arrears: a.total_paid_in_arrears ? String(a.total_paid_in_arrears) : "",
            arrears_count: a.arrears_count ? String(a.arrears_count) : "",
            arrears_1_7_days: a.arrears_1_7_days ? String(a.arrears_1_7_days) : "",
            arrears_8_30_days: a.arrears_8_30_days ? String(a.arrears_8_30_days) : "",
            arrears_30_plus_days: a.arrears_30_plus_days ? String(a.arrears_30_plus_days) : "",
            loan_book_maturity_pct: a.loan_book_maturity_pct ? String(a.loan_book_maturity_pct) : ""
          }));
          setAutofilled(true);
        }
      })
      .catch((err) => console.error("Prefill failed:", err))
      .finally(() => setPrefilling(false));
  }, [open, reportDateStr]);

  const handleUseArrearsData = async () => {
    setPullingArrears(true);
    try {
      const res = await getArrearsLiveStats();
      const a = res.arrears || {};
      const cf = res.carried_forward;
      setFormData((prev) => ({
        ...prev,
        total_arrears: a.total_arrears ? String(a.total_arrears) : prev.total_arrears,
        principal_in_arrears: a.principal_in_arrears ? String(a.principal_in_arrears) : prev.principal_in_arrears,
        total_paid_in_arrears: a.total_paid_in_arrears ? String(a.total_paid_in_arrears) : prev.total_paid_in_arrears,
        arrears_count: a.arrears_count ? String(a.arrears_count) : prev.arrears_count,
        arrears_1_7_days: String(a.arrears_1_7_days ?? ""),
        arrears_8_30_days: String(a.arrears_8_30_days ?? ""),
        arrears_30_plus_days: String(a.arrears_30_plus_days ?? ""),
        ...(cf ? {
          total_open_loans: cf.total_open_loans ? String(cf.total_open_loans) : prev.total_open_loans,
          total_receivables: cf.total_receivables ? String(cf.total_receivables) : prev.total_receivables,
          loan_book_maturity_pct: cf.loan_book_maturity_pct ? String(cf.loan_book_maturity_pct) : prev.loan_book_maturity_pct,
        } : {})
      }));
      const snapTime = res.snapshot?.uploaded_at
        ? new Date(res.snapshot.uploaded_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : null;
      setArrearsSource({
        count: a.arrears_count,
        snapTime,
        carriedFrom: cf?.from_date
          ? new Date(cf.from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : null
      });
      toast.success(`Pulled ${a.arrears_count} arrears accounts from today's arrears list`);
    } catch (err) {
      toast.error("Failed to pull arrears data");
      console.error(err);
    } finally {
      setPullingArrears(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.loan_due || !formData.cash_collected) {
      toast.error("Loan Due and Cash Collected are required");
      return;
    }
    
    setSubmitting(true);
    try {
      await submitDailyReport({
        date: reportDateStr,
        loan_due: parseFloat(formData.loan_due),
        cash_collected: parseFloat(formData.cash_collected),
        total_open_loans: formData.total_open_loans ? parseInt(formData.total_open_loans) : null,
        total_receivables: formData.total_receivables ? parseFloat(formData.total_receivables) : null,
        total_arrears: formData.total_arrears ? parseFloat(formData.total_arrears) : null,
        principal_in_arrears: formData.principal_in_arrears ? parseFloat(formData.principal_in_arrears) : null,
        total_paid_in_arrears: formData.total_paid_in_arrears ? parseFloat(formData.total_paid_in_arrears) : null,
        arrears_count: formData.arrears_count ? parseInt(formData.arrears_count) : null,
        arrears_1_7_days: formData.arrears_1_7_days ? parseInt(formData.arrears_1_7_days) : null,
        arrears_8_30_days: formData.arrears_8_30_days ? parseInt(formData.arrears_8_30_days) : null,
        arrears_30_plus_days: formData.arrears_30_plus_days ? parseInt(formData.arrears_30_plus_days) : null,
        loan_book_maturity_pct: formData.loan_book_maturity_pct ? parseFloat(formData.loan_book_maturity_pct) : null
      });
      toast.success(`Collections report posted for ${new Date(reportDateStr).toLocaleDateString('en-GB')}`);
      onOpenChange(false);
      setFormData(EMPTY_REPORT_FORM);
      onSubmitted?.();
    } catch (err) {
      toast.error("Failed to post collections report");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "£0.00";
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(val);
  };

  // Calculate derived values for preview
  const collectionPct = formData.loan_due && formData.cash_collected 
    ? ((parseFloat(formData.cash_collected) / parseFloat(formData.loan_due)) * 100).toFixed(1) : 0;
  const variance = formData.loan_due && formData.cash_collected 
    ? parseFloat(formData.cash_collected) - parseFloat(formData.loan_due) : 0;
  const arrearsPct = formData.total_receivables && formData.total_arrears
    ? ((parseFloat(formData.total_arrears) / parseFloat(formData.total_receivables)) * 100).toFixed(2) : 0;
  const pctAcctsInArrears = formData.total_open_loans && formData.arrears_count
    ? ((parseInt(formData.arrears_count) / parseInt(formData.total_open_loans)) * 100).toFixed(2) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <FileText className="w-5 h-5" />
            Post Collections Report
          </DialogTitle>
          <DialogDescription>
            Collections figures for {new Date(reportDateStr).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} (previous working day).
            Arrears are as of this morning's list — accounts that missed yesterday enter Day 1 today.
          </DialogDescription>
        </DialogHeader>

        {prefilling ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Checking for filed arrears data...
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {autofilled ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex-1" data-testid="arrears-autofill-banner">
                  <CheckCheck className="w-4 h-4 shrink-0" />
                  Arrears already filed for this date — autofilled below. Just complete the collections figures.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground flex-1">
                  {arrearsSource
                    ? `Pulled ${arrearsSource.count} accounts from today's arrears list${arrearsSource.snapTime ? ` (uploaded ${arrearsSource.snapTime})` : ""}${arrearsSource.carriedFrom ? ` • Open loans / receivables carried from ${arrearsSource.carriedFrom} report` : ""}`
                    : "Pull the arrears side straight from today's arrears list, then complete the collections figures."}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleUseArrearsData}
                disabled={pullingArrears}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
                data-testid="use-arrears-data-btn"
              >
                {pullingArrears ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendUp className="w-4 h-4 mr-2" />}
                Use today's arrears data
              </Button>
            </div>
            {/* Core Fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Loan Due</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.loan_due}
                  onChange={(e) => setFormData({...formData, loan_due: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Cash Collected *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cash_collected}
                  onChange={(e) => setFormData({...formData, cash_collected: e.target.value})}
                  placeholder="Enter amount"
                  className="mt-1 text-sm border-emerald-300 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase font-medium">Collection %</label>
                <div className="mt-1 h-9 flex items-center px-3 bg-slate-100 rounded-md text-sm font-medium">
                  {collectionPct}%
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase font-medium">Variance</label>
                <div className={`mt-1 h-9 flex items-center px-3 bg-slate-100 rounded-md text-sm font-medium ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(variance)}
                </div>
              </div>
            </div>

            {/* Loan Book Fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Total Receivables</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_receivables}
                  onChange={(e) => setFormData({...formData, total_receivables: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Total Arrears</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_arrears}
                  onChange={(e) => setFormData({...formData, total_arrears: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Principal (In Arrears)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.principal_in_arrears}
                  onChange={(e) => setFormData({...formData, principal_in_arrears: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Paid (In Arrears)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_paid_in_arrears}
                  onChange={(e) => setFormData({...formData, total_paid_in_arrears: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
            </div>

              {/* Arrears & Ageing */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="text-xs text-slate-500 uppercase font-medium">% Arrears</label>
                <div className="mt-1 h-9 flex items-center px-3 bg-slate-100 rounded-md text-sm font-medium">
                  {arrearsPct}%
                </div>
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium"># in Arrears</label>
                <Input
                  type="number"
                  value={formData.arrears_count}
                  onChange={(e) => setFormData({...formData, arrears_count: e.target.value})}
                  placeholder="0"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Day 1-7</label>
                <Input
                  type="number"
                  value={formData.arrears_1_7_days}
                  onChange={(e) => setFormData({...formData, arrears_1_7_days: e.target.value})}
                  placeholder="0"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Day 8-30</label>
                <Input
                  type="number"
                  value={formData.arrears_8_30_days}
                  onChange={(e) => setFormData({...formData, arrears_8_30_days: e.target.value})}
                  placeholder="0"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Day 30+</label>
                <Input
                  type="number"
                  value={formData.arrears_30_plus_days}
                  onChange={(e) => setFormData({...formData, arrears_30_plus_days: e.target.value})}
                  placeholder="0"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Maturity %</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.loan_book_maturity_pct}
                  onChange={(e) => setFormData({...formData, loan_book_maturity_pct: e.target.value})}
                  placeholder="0.00"
                  className="mt-1 text-sm"
                />
              </div>
            </div>

            {/* Open Loans & % Accts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-emerald-700 uppercase font-medium">Total Open Loans</label>
                <Input
                  type="number"
                  value={formData.total_open_loans}
                  onChange={(e) => setFormData({...formData, total_open_loans: e.target.value})}
                  placeholder="0"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase font-medium">% Accts in Arrears</label>
                <div className="mt-1 h-9 flex items-center px-3 bg-slate-100 rounded-md text-sm font-medium">
                  {pctAcctsInArrears}%
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="post-report-cancel-btn">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || !formData.loan_due || !formData.cash_collected}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="post-report-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
                Post Report
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Kept for Loan Repayments later (Dean, Oct 2026): MTD / WTD / Yesterday history cards. Not rendered on this page. ──
// eslint-disable-next-line no-unused-vars
function PeriodHistoryCards({ reportSummary, selectedDate, onUpdated }) {
  const [editingReport, setEditingReport] = useState(false);
  const [editLoanDue, setEditLoanDue] = useState("");
  const [editCashCollected, setEditCashCollected] = useState("");
  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  const loadData = () => onUpdated?.();
  if (!reportSummary) return null;
  return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Month to Date */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="py-2.5 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-700">Month to Date</span>
              </div>
              <Badge variant="outline" className="border-purple-300 text-purple-600 text-[10px] px-1.5">
                {reportSummary.mtd?.days || 0} days
              </Badge>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              <div>
                <p className="text-[10px] text-purple-600 uppercase">Due</p>
                <p className="text-sm font-bold text-purple-700">{formatCurrency(reportSummary.mtd?.loan_due)}</p>
              </div>
              <div>
                <p className="text-[10px] text-purple-600 uppercase">Collected</p>
                <p className="text-sm font-bold text-purple-700">{formatCurrency(reportSummary.mtd?.cash_collected)}</p>
              </div>
              <div>
                <p className="text-[10px] text-purple-600 uppercase">Coll %</p>
                <p className="text-sm font-bold text-purple-700">{(reportSummary.mtd?.collection_pct || 0).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-[10px] text-purple-600 uppercase">Variance</p>
                <p className={`text-sm font-bold ${(reportSummary.mtd?.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportSummary.mtd?.variance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Week to Date */}
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="py-2.5 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-700">Week to Date</span>
              </div>
              <Badge variant="outline" className="border-indigo-300 text-indigo-600 text-[10px] px-1.5">
                {reportSummary.wtd?.days || 0} days
              </Badge>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              <div>
                <p className="text-[10px] text-indigo-600 uppercase">Due</p>
                <p className="text-sm font-bold text-indigo-700">{formatCurrency(reportSummary.wtd?.loan_due)}</p>
              </div>
              <div>
                <p className="text-[10px] text-indigo-600 uppercase">Collected</p>
                <p className="text-sm font-bold text-indigo-700">{formatCurrency(reportSummary.wtd?.cash_collected)}</p>
              </div>
              <div>
                <p className="text-[10px] text-indigo-600 uppercase">Coll %</p>
                <p className="text-sm font-bold text-indigo-700">{(reportSummary.wtd?.collection_pct || 0).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-[10px] text-indigo-600 uppercase">Variance</p>
                <p className={`text-sm font-bold ${(reportSummary.wtd?.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportSummary.wtd?.variance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Yesterday (Editable) */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="py-2.5 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-700">Yesterday</span>
              </div>
              {!editingReport ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-200"
                  onClick={() => {
                    setEditingReport(true);
                    setEditLoanDue(reportSummary.yesterday?.loan_due?.toString() || "");
                    setEditCashCollected(reportSummary.yesterday?.cash_collected?.toString() || "");
                  }}
                >
                  <Edit3 className="w-3.5 h-3.5 mr-1" />
                  Update
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-green-600 hover:bg-green-100"
                    onClick={async () => {
                      try {
                        const yesterday = new Date(selectedDate);
                        yesterday.setDate(yesterday.getDate() - 1);
                        const yesterdayStr = yesterday.toISOString().split('T')[0];
                        await updateCollectionsReport(
                          yesterdayStr,
                          parseFloat(editLoanDue) || 0,
                          parseFloat(editCashCollected) || 0
                        );
                        toast.success("Report updated");
                        setEditingReport(false);
                        loadData(true);
                      } catch (err) {
                        toast.error("Failed to update");
                      }
                    }}
                  >
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-slate-500 hover:bg-slate-100"
                    onClick={() => setEditingReport(false)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
        
            {editingReport ? (
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <div>
                  <label className="text-[10px] text-amber-600 uppercase">Loan Due</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editLoanDue}
                    onChange={(e) => setEditLoanDue(e.target.value)}
                    className="h-8 mt-0.5"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-amber-600 uppercase">Cash Collected</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editCashCollected}
                    onChange={(e) => setEditCashCollected(e.target.value)}
                    className="h-8 mt-0.5"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2 mt-1.5">
                <div>
                  <p className="text-[10px] text-amber-600 uppercase">Due</p>
                  <p className="text-sm font-bold text-amber-700">{formatCurrency(reportSummary.yesterday?.loan_due)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-amber-600 uppercase">Collected</p>
                  <p className="text-sm font-bold text-amber-700">{formatCurrency(reportSummary.yesterday?.cash_collected)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-amber-600 uppercase">Coll %</p>
                  <p className="text-sm font-bold text-amber-700">{(reportSummary.yesterday?.collection_pct || 0).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-[10px] text-amber-600 uppercase">Variance</p>
                  <p className={`text-sm font-bold ${(reportSummary.yesterday?.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportSummary.yesterday?.variance)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );
}

// Dean, Oct 2026: this page is about TODAY — due vs received vs still due. History cards, the period trend analysis
// and "Post Collections Report" are kept in code (PeriodHistoryCards / CollectionsPeriodAnalysis / SubmitDailyReportDialog)
// but switched off here until they move to Loan Repayments.
const SHOW_HISTORY = false;
const SHOW_POST_REPORT = false;

export default function DailyCollectionsReport() {
  // AU desk (3 Nov 2026): this page is the AU Collection Report only — "today's" AU repayments live on Loan Repayments.
  const { currentRegion } = useRegion();
  const aus = currentRegion === "AUS";
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // Dean, 28 Oct 2026: "Today" (due vs received vs still due) or the monthly "Collection Report" (the team's sheet, LoanDisk-fed)
  const [tabState, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "report" ? "report" : "today"));
  const tab = aus ? "report" : tabState;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [error, setError] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);

  // Manual matching
  const [matchingLoan, setMatchingLoan] = useState(null);
  const [matchSearchQuery, setMatchSearchQuery] = useState("");
  const [matchingInProgress, setMatchingInProgress] = useState(false);

  // Post report dialog (hidden on this page — see SHOW_POST_REPORT)
  const [postReportOpen, setPostReportOpen] = useState(false);

  // P2P payment matching
  const [matchingP2P, setMatchingP2P] = useState(null);
  const [p2pMatchBusy, setP2pMatchBusy] = useState(false);

  // Pre-chase wizard + per-loan facts (arrears / iteration / disbursement) for the Awaiting table and the wizard filter
  const [prechaseOpen, setPrechaseOpen] = useState(false);
  const [chased, setChased] = useState({});
  const [facts, setFacts] = useState(null);
  const loadChased = async () => {
    try { setChased((await getPrechaseStatus(selectedDate)).chased || {}); } catch { setChased({}); }
  };
  const loadFacts = async (loans) => {
    if (!loans?.length) { setFacts({}); return; }
    try { setFacts((await getPrechaseFacts({ date: selectedDate, loans })).facts || {}); } catch { setFacts({}); }
  };

  const [lastUpdated, setLastUpdated] = useState(null);
  const loadData = async (showRefreshToast = false, silent = false) => {
    try {
      if (!silent) { if (showRefreshToast) setRefreshing(true); else setLoading(true); }
      setError(null);
      const [matchResult, summaryResult] = await Promise.all([
        matchPaymentsToLoans(selectedDate),
        SHOW_HISTORY ? getCollectionsReportSummary(selectedDate).catch(() => null) : Promise.resolve(null),
        loadChased(),
      ]);
      setMatchData(matchResult);
      setReportSummary(summaryResult);
      setLastUpdated(new Date());
      if (showRefreshToast) toast.success("Data refreshed");
      if (!silent) setFacts(null);
      loadFacts(matchResult?.unmatched_loans || []);
    } catch (err) {
      if (silent) return;
      console.error("Error loading collections data:", err);
      setError(err.message || "Failed to load data");
      toast.error("Failed to load collections data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (aus) { setLoading(false); return; } loadData(); }, [selectedDate, aus]); // eslint-disable-line react-hooks/exhaustive-deps
  // The bank feed is pulled on a schedule in the background — re-read the report every minute so new credits show up
  // without anyone pressing a button (today only, and only while the tab is visible).
  useEffect(() => {
    if (aus || selectedDate !== new Date().toISOString().split('T')[0]) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") loadData(false, true); }, 60000);
    return () => clearInterval(t);
  }, [selectedDate, aus]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return dateStr; }
  };

  const sortedUnmatchedTransactions = [...(matchData?.unmatched_transactions || [])]
    .sort((a, b) => (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0));

  // Score a transaction's likelihood of belonging to a loan (Find Match ranking)
  const scoreTxnForLoan = (loan, txn) => {
    if (!loan) return 0;
    let score = 0;
    const due = parseFloat(loan.due_amount) || 0;
    const scheduled = parseFloat(loan.scheduled_amount) || due;
    const amt = parseFloat(txn.amount) || 0;
    if ((due > 0 && Math.abs(due - amt) < 0.01) || (scheduled > 0 && Math.abs(scheduled - amt) < 0.01)) score += 50;
    else if (due > 0 && amt > 0 && Math.abs(due - amt) / due < 0.1) score += 20;
    const sfMatch = (loan.sf_number || "").toUpperCase().match(/SF[\s\-_]*(\d{3,6})/);
    const txnText = `${txn.description || ""} ${txn.reference || ""}`.toUpperCase();
    if (sfMatch && txnText.replace(/[\s\-_]/g, "").includes(`SF${sfMatch[1]}`)) score += 45;
    const noise = ["LTD", "LIMITED", "THE", "AND", "STO", "BGC", "FPI", "PLC", "LLP"];
    const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z ]/g, " ").split(/\s+/)
      .filter((w) => w.length > 2 && !noise.includes(w));
    const loanTokens = new Set(norm(loan.borrower_name));
    const overlap = norm(txnText).filter((w) => loanTokens.has(w)).length;
    if (overlap) score += Math.min(30, overlap * 15);
    return score;
  };

  const filteredTransactions = sortedUnmatchedTransactions
    .filter((txn) => {
      if (!matchSearchQuery) return true;
      const query = matchSearchQuery.toLowerCase();
      return (txn.description || "").toLowerCase().includes(query) || (txn.reference || "").toLowerCase().includes(query) || String(txn.amount || "").includes(query);
    })
    .map((txn) => ({ ...txn, _score: scoreTxnForLoan(matchingLoan, txn) }))
    .sort((a, b) => b._score - a._score);

  const handleP2PMatch = async (p2p, txn) => {
    setP2pMatchBusy(true);
    try {
      await matchP2PPayment({ case_id: p2p.case_id, p2p_id: p2p.p2p_id, transaction_id: txn.id, date: selectedDate, amount: parseFloat(txn.amount) || null });
      toast.success(`Matched ${p2p.customer_name}'s promise to £${txn.amount}`);
      setMatchingP2P(null);
      loadData(true);
    } catch (err) {
      toast.error("Failed to match P2P payment");
    } finally {
      setP2pMatchBusy(false);
    }
  };

  const handleP2PUnmatch = async (p2p) => {
    try {
      await unmatchP2PPayment(p2p.matched_transaction.id, selectedDate);
      toast.success("P2P match removed");
      loadData(true);
    } catch (err) {
      toast.error("Failed to remove P2P match");
    }
  };

  const handleManualMatch = async (loan, transaction) => {
    setMatchingInProgress(true);
    try {
      const res = await manualMatchPayment(loan.loan_id || loan.sf_number, transaction.id, selectedDate, {
        loanSf: loan.sf_number, borrowerName: loan.borrower_name, transactionDescription: transaction.description,
      });
      toast.success(res?.learned_alias ? `Matched ${loan.borrower_name} — this payment reference will auto-match in future` : `Matched ${loan.borrower_name} with transaction`);
      setMatchingLoan(null);
      setMatchSearchQuery("");
      loadData(true);
    } catch (err) {
      toast.error("Failed to create manual match");
    } finally {
      setMatchingInProgress(false);
    }
  };

  const handleIrrelevant = async (txn) => {
    try {
      await markTransactionIrrelevant(txn.id, "Internal transfer");
      toast.success("Credit hidden as irrelevant");
      loadData(true);
    } catch (err) {
      toast.error("Failed to mark as irrelevant");
    }
  };

  const summary = matchData?.summary || {};
  // Due = what LoanDisk says is still owed today (part-paid loans owe the remainder); the scheduled instalment is kept alongside
  const withStillDue = (l) => ({ ...l, base_sf: baseSf(l.sf_number), scheduled_amount: l.due_amount, due_amount: l.still_due ?? l.due_amount });
  const unmatchedLoans = (matchData?.unmatched_loans || []).map(withStillDue);
  const coveredLoans = (matchData?.covered_loans || []).map(withStillDue);

  return (
    <div className="space-y-5" data-testid="daily-collections-report">
      {/* Manual Match Dialog */}
      <Dialog open={!!matchingLoan} onOpenChange={(open) => !open && setMatchingLoan(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="find-match-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-emerald-600" />
              Match payment for {matchingLoan?.borrower_name}
            </DialogTitle>
            <DialogDescription>
              Amount due: <span className="font-mono font-semibold text-foreground">{formatCurrency(matchingLoan?.due_amount)}</span>
              {" • "}<span className="font-mono">{matchingLoan?.sf_number}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by description, reference, or amount..." value={matchSearchQuery} onChange={(e) => setMatchSearchQuery(e.target.value)} className="pl-10" autoFocus data-testid="find-match-search" />
            </div>
            <div className="flex-1 overflow-y-auto divide-y rounded-md border min-h-[300px]">
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><p>No matching transactions found</p></div>
              ) : filteredTransactions.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-emerald-50/50 cursor-pointer group" onClick={() => !matchingInProgress && handleManualMatch(matchingLoan, txn)} data-testid={`find-match-row-${txn.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{txn.description || "Bank transfer"}</p>
                      {txn._score >= 45 && <QuietTag tone="emerald" testId="likely-match-badge">Likely match</QuietTag>}
                    </div>
                    <p className="text-xs text-muted-foreground">{txn.bank_account ? `${txn.bank_account} • ` : ""}{formatDate(txn.date)}</p>
                  </div>
                  <p className="font-mono font-semibold tabular-nums">{formatCurrency(txn.amount)}</p>
                  <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity" disabled={matchingInProgress}>
                    {matchingInProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : "Match"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {SHOW_POST_REPORT && (
        <SubmitDailyReportDialog selectedDate={selectedDate} open={postReportOpen} onOpenChange={setPostReportOpen} onSubmitted={() => loadData(true)} />
      )}
      <PreChaseWizard open={prechaseOpen} onOpenChange={setPrechaseOpen} date={selectedDate} chased={chased} facts={facts} loans={unmatchedLoans} onSent={loadChased} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2" data-testid="page-title">
              {aus ? "Collection Report" : "Daily Collections"}
              {aus && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200" data-testid="page-region-chip">🇦🇺 Australia</span>}
            </h1>
            <p className="text-xs text-slate-500">{aus ? <>Daily cash, arrears and loan book for the AU book — fed by LoanDisk AU and GoCardless. Today's repayments live on <Link to="/aus/collections/loan-repayments" className="underline decoration-dotted hover:text-slate-800" data-testid="link-loan-repayments">Loan Repayments</Link>.</> : tab === "report" ? "Daily cash, arrears and loan book — the collection report, fed by LoanDisk" : "Due today, what has come in, what is still owed"}</p>
          </div>
          {!aus && (
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="daily-collections-tabs">
            <button type="button" onClick={() => setTab("today")} data-testid="tab-today"
              className={`h-7 rounded px-3 text-xs font-semibold transition-colors ${tab === "today" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Today</button>
            <button type="button" onClick={() => setTab("report")} data-testid="tab-collection-report"
              className={`h-7 rounded px-3 text-xs font-semibold transition-colors ${tab === "report" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Collection Report</button>
          </div>
          )}
        </div>
        {tab === "today" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="h-8 w-40 text-sm" data-testid="report-date-input" />
          <Button variant="outline" size="sm" className="h-8" onClick={() => loadData(true)} disabled={refreshing} data-testid="refresh-page-button">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Refresh
          </Button>
          {lastUpdated && (
            <span className="text-[10px] font-mono tabular-nums text-slate-400" title="The report re-reads itself every minute while this tab is open" data-testid="report-last-updated">
              updated {lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <BankFeedChip onRefreshed={() => loadData(true)} />
          {SHOW_POST_REPORT && (
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setPostReportOpen(true)} data-testid="post-collections-report-btn">
              <FileText className="w-4 h-4 mr-2" />Post Collections Report
            </Button>
          )}
        </div>
        )}
      </div>

      {tab === "report" ? (
        <CollectionReportTab />
      ) : loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500" data-testid="report-loading">
          <Loader2 className="w-6 h-6 animate-spin" /><span className="ml-3 text-sm">Loading today's collections…</span>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center" data-testid="report-error">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
          <p className="text-red-700">{error}</p>
          <Button className="mt-4" variant="outline" onClick={() => loadData()}>Try again</Button>
        </div>
      ) : (
        <>
          {SHOW_HISTORY && <PeriodHistoryCards reportSummary={reportSummary} selectedDate={selectedDate} onUpdated={() => loadData(true)} />}
          {SHOW_HISTORY && <CollectionsPeriodAnalysis />}

          <DailyReportStats summary={summary} />

          {/* Promises to Pay due today */}
          {matchData?.p2ps_due?.length > 0 && (
            <section className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-3" data-testid="p2ps-due-card">
              <h2 className="text-sm font-semibold text-slate-900">Promises to pay due today <span className="font-mono text-xs font-normal text-slate-500">· {matchData.p2ps_due.length}</span></h2>
              <div className="mt-2 divide-y divide-slate-100">
                {matchData.p2ps_due.map((p) => (
                  <div key={p.p2p_id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-slate-900">{p.customer_name}</span> <span className="font-mono text-xs text-slate-500">{p.sf_number}</span>
                      <span className="text-xs text-slate-500">{p.handler ? ` · ${p.handler}` : ""}{p.method ? ` · ${p.method.replace(/_/g, " ")}` : ""}{p.notes ? ` · ${p.notes}` : ""}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.promised_amount != null && <span className="font-mono text-sm font-semibold tabular-nums">{formatCurrency(p.promised_amount)}</span>}
                      {p.matched_transaction ? (
                        <>
                          <QuietTag tone="emerald">Paid {formatCurrency(p.matched_transaction.amount)}</QuietTag>
                          <button onClick={() => handleP2PUnmatch(p)} className="text-slate-400 hover:text-red-600" title="Remove this match" data-testid={`p2p-unmatch-${p.p2p_id}`}><XCircle className="w-4 h-4" /></button>
                        </>
                      ) : p.payment_seen_today ? <QuietTag tone="emerald">Payment seen today</QuietTag>
                        : p.status === "kept" ? <QuietTag tone="emerald">Kept</QuietTag>
                        : p.status === "broken" ? <QuietTag tone="amber">Broken</QuietTag>
                        : (
                          <>
                            <QuietTag tone="amber">Awaiting</QuietTag>
                            <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={() => setMatchingP2P(p)} data-testid={`p2p-match-btn-${p.p2p_id}`}>Match payment</Button>
                          </>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* P2P payment match dialog */}
          <Dialog open={!!matchingP2P} onOpenChange={(v) => !v && setMatchingP2P(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="p2p-match-dialog">
              <DialogHeader>
                <DialogTitle>Match payment — {matchingP2P?.customer_name} ({matchingP2P?.sf_number})</DialogTitle>
                <DialogDescription>Promised {matchingP2P?.promised_amount != null ? formatCurrency(matchingP2P.promised_amount) : "—"} • pick the bank credit that pays this promise</DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto divide-y rounded-md border">
                {sortedUnmatchedTransactions
                  .map((txn) => ({ ...txn, _score: matchingP2P ? scoreTxnForLoan({ due_amount: matchingP2P.promised_amount, sf_number: matchingP2P.sf_number, borrower_name: matchingP2P.customer_name }, txn) : 0 }))
                  .sort((a, b) => b._score - a._score)
                  .map((txn) => (
                    <div key={txn.id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-emerald-50/50">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{txn.description || "Bank transfer"}</p>
                          {txn._score >= 45 && <QuietTag tone="emerald">Likely match</QuietTag>}
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(txn.date)}</p>
                      </div>
                      <span className="font-mono text-sm font-semibold tabular-nums shrink-0">{formatCurrency(txn.amount)}</span>
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 shrink-0" disabled={p2pMatchBusy} onClick={() => handleP2PMatch(matchingP2P, txn)} data-testid={`p2p-pick-txn-${txn.id}`}>
                        {p2pMatchBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Match"}
                      </Button>
                    </div>
                  ))}
                {sortedUnmatchedTransactions.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No unmatched credits available</p>}
              </div>
            </DialogContent>
          </Dialog>

          {/* Awaiting payment (hero) | Unmatched credits */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
            <div className="xl:col-span-7">
              <AwaitingPaymentTable loans={unmatchedLoans} chased={chased} facts={facts} total={summary.outstanding_amount} partPaidCount={summary.part_paid_count}
                onFindMatch={(loan) => { setMatchingLoan(loan); setMatchSearchQuery(""); }} onPrechase={() => setPrechaseOpen(true)} />
            </div>
            <div className="xl:col-span-5">
              <UnmatchedTransactionsTable txns={matchData?.unmatched_transactions || []} categories={summary.unmatched_categories} onIrrelevant={handleIrrelevant} />
            </div>
          </div>

          <CoveredCollapsible covered={coveredLoans} total={summary.covered_amount} facts={facts} />
          <MatchedCollapsible matched={matchData?.matched || []} total={summary.total_matched_amount} />
        </>
      )}
    </div>
  );
}
